import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import { DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  callSign: z.string().min(1, { error: 'Callsign is required' }),
  hdrMulticastAddress: z
    .string()
    .transform((value) => (value?.length === 0 ? null : value))
    .optional()
    .nullable()
    .refine((value) => value === null || z.url().safeParse(value).success, {
      error: 'HDR Multicast Address must be valid url',
    }),
  multicastAddress: z.url({ error: 'Multicast Address must be valid url' }),
  priority: z.string().min(1, { error: 'Priority is required' }),
  sourceLocation: z.string().min(1, { error: 'Source Location is required' }),
});

export type MulticastMappingFormValues = z.infer<typeof formSchema>;

export interface MulticastMappingFormProps {
  defaultValues?: MulticastMappingFormValues;
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (values: MulticastMappingFormValues) => void;
}

export function MulticastMappingForm({
  defaultValues,
  disabled = false,
  onCancel,
  onSubmit,
}: MulticastMappingFormProps) {
  const { priority, sourceLocations } =
    useAppSelector(selectToolsConfig).btcTools.pages.multicastMapping;

  const form = useForm<MulticastMappingFormValues>({
    defaultValues: {
      callSign: '',
      hdrMulticastAddress: '',
      multicastAddress: '',
      priority: priority[0],
      sourceLocation: sourceLocations[0],
      ...defaultValues,
    },
    disabled,
    resolver: zodResolver(formSchema),
  });

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        onReset={() => {
          form.reset();
          onCancel();
        }}
        onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="callSign"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Call Sign</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Priority</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full" disabled={field.disabled}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {priority.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="multicastAddress"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Multicast Address</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="hdrMulticastAddress"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>HDR Multicast Address</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="sourceLocation"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Source Location</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full" disabled={field.disabled}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sourceLocations.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <DialogFooter>
          <Button disabled={form.formState.disabled} type="reset" variant="secondary">
            Cancel
          </Button>
          <Button
            aria-label="submit"
            disabled={form.formState.disabled}
            loading={form.formState.disabled}
            type="submit">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
